"use client"

import { useEffect, useRef } from "react"
import { useSearchParams } from "next/navigation"
import { useUserContext } from "src/contexts/user"
import { useWalletAuth } from "src/hooks/useWalletAuth"
import { sendGTMEvent } from "@next/third-parties/google"
import { decryptEmail } from "src/utils/decryptEmail"

/**
 * Component to automatically show Privy login modal for dgbeta traffic
 * Triggers Privy connect wallet popup 10 seconds after landing when:
 * - utm_source parameter equals "dgbeta" or stored in localStorage
 * - User has not previously dismissed the modal or interacted with it
 * If utm_email is present, it will be decrypted and prefilled in the login modal
 */
const DgbetaAutoLogin: React.FC = () => {
  const searchParams = useSearchParams()
  const { currentUser } = useUserContext()
  const { connectOrCreateWallet } = useWalletAuth()
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const hasTriggeredRef = useRef(false)
  const modalTriggeredRef = useRef(false)

  const DISMISSAL_KEY = "dgbeta_auto_login_dismissed"
  const UTM_SOURCE_KEY = "dgbeta_utm_source"
  const UTM_EMAIL_KEY = "dgbeta_utm_email"
  const MODAL_SHOWN_KEY = "dgbeta_modal_shown" // New key to track if modal was ever shown

  // Store UTM params on component mount
  useEffect(() => {
    hasTriggeredRef.current = false

    if (typeof window !== "undefined") {
      const utmSource = searchParams?.get("utm_source")
      const utmEmail = searchParams?.get("utm_email")

      if (utmSource === "dgbeta") {
        window.localStorage.setItem(UTM_SOURCE_KEY, utmSource)
      }

      if (utmEmail) {
        window.localStorage.setItem(UTM_EMAIL_KEY, utmEmail)
      }

      ;(window as any).resetDgbetaAutoLogin = () => {
        hasTriggeredRef.current = false
        window.localStorage.removeItem(DISMISSAL_KEY)
        window.localStorage.removeItem(MODAL_SHOWN_KEY)
        console.log("DgbetaAutoLogin reset")
      }
      ;(window as any).clearDgbetaUtmSource = () => {
        window.localStorage.removeItem(UTM_SOURCE_KEY)
      }
    }
  }, [searchParams])

  useEffect(() => {
    hasTriggeredRef.current = false

    if (currentUser?.id) {
      return
    }

    if (typeof window !== "undefined") {
      const hasDismissed = window.localStorage.getItem(DISMISSAL_KEY) === "true"
      const modalAlreadyShown =
        window.localStorage.getItem(MODAL_SHOWN_KEY) === "true"

      // If modal was already shown in this session, don't show it again
      if (hasDismissed || modalAlreadyShown) {
        console.log(
          "Modal blocked - dismissed:",
          hasDismissed,
          "already shown:",
          modalAlreadyShown
        )
        return
      }
    }

    const isDgbetaTraffic = () => {
      if (typeof window !== "undefined") {
        const storedUtmSource = window.localStorage.getItem(UTM_SOURCE_KEY)
        if (storedUtmSource === "dgbeta") {
          return true
        }
      }

      const utmSource = searchParams?.get("utm_source")
      if (utmSource === "dgbeta") {
        return true
      }

      if (typeof window !== "undefined") {
        const currentUrl = window.location.href.toLowerCase()
        if (currentUrl.includes("dgbeta")) {
          return true
        }
      }

      return false
    }

    const getEncryptedEmail = () => {
      if (typeof window !== "undefined") {
        const storedEmail = window.localStorage.getItem(UTM_EMAIL_KEY)
        if (storedEmail) {
          return storedEmail
        }
      }
      return searchParams?.get("utm_email")
    }

    if (!isDgbetaTraffic() || hasTriggeredRef.current) {
      return
    }

    hasTriggeredRef.current = true

    timeoutRef.current = setTimeout(async () => {
      // Final check before showing modal
      if (
        currentUser?.id ||
        window.localStorage.getItem(MODAL_SHOWN_KEY) === "true"
      ) {
        return
      }

      console.log("Triggering modal...")
      modalTriggeredRef.current = true

      // Immediately mark that we've shown the modal to prevent retriggering
      window.localStorage.setItem(MODAL_SHOWN_KEY, "true")

      if (!currentUser?.id) {
        try {
          const encryptedEmail = getEncryptedEmail()
          let decryptedEmail: string | null = null

          if (encryptedEmail) {
            try {
              decryptedEmail = await decryptEmail(encryptedEmail)
            } catch (error) {
              // Continue without email prefill on decryption failure
            }
          }

          const loginOptions = decryptedEmail
            ? {
                prefill: {
                  type: "email" as const,
                  value: decryptedEmail,
                },
              }
            : undefined

          connectOrCreateWallet(loginOptions)
          sendGTMEvent({
            event: "wallet_connect_initiated",
            is_betslip: false,
            section: "dgbeta_auto_login",
            login_toolkit: "privy",
          })

          // Simple monitoring just for analytics - doesn't affect retriggering
          let modalShown = false
          let checkCount = 0
          const maxChecks = 60 // Reduced checks since we're not relying on this for prevention

          const setDismissalFlag = (method: string) => {
            window.localStorage.setItem(DISMISSAL_KEY, "true")
            sendGTMEvent({
              event: "wallet_connect_dismissed",
              is_betslip: false,
              section: "dgbeta_auto_login",
              login_toolkit: "privy",
              dismissal_method: method,
            })
          }

          const checkForAnalytics = () => {
            checkCount++

            const privyModal =
              document.querySelector("[data-privy-modal]") ||
              document.querySelector('[class*="privy-modal"]') ||
              document.querySelector('[role="dialog"][class*="privy"]') ||
              document.querySelector('iframe[src*="privy"]') ||
              document.querySelector('[class*="PrivyModal"]')

            if (privyModal && !modalShown) {
              modalShown = true
              console.log("Modal detected for analytics")

              // Simple dismissal detection for analytics only
              const addDismissalListeners = () => {
                const closeButton = document.querySelector(
                  'button[aria-label="close modal"], button[aria-label="Close"], [data-testid="close-button"]'
                )
                if (closeButton) {
                  closeButton.addEventListener(
                    "click",
                    () => setDismissalFlag("close_button"),
                    { once: true }
                  )
                }

                const handleEscKey = (event: KeyboardEvent) => {
                  if (event.key === "Escape") {
                    setDismissalFlag("escape_key")
                    document.removeEventListener("keydown", handleEscKey)
                  }
                }
                document.addEventListener("keydown", handleEscKey)

                const modalOverlay =
                  privyModal.closest('[role="dialog"]')?.parentElement
                if (modalOverlay) {
                  const handleOverlayClick = (event: Event) => {
                    if (event.target === modalOverlay) {
                      setDismissalFlag("overlay_click")
                      modalOverlay.removeEventListener(
                        "click",
                        handleOverlayClick
                      )
                    }
                  }
                  modalOverlay.addEventListener("click", handleOverlayClick, {
                    once: true,
                  })
                }
              }

              setTimeout(addDismissalListeners, 500)
            }

            // Continue checking for a short time for analytics
            if (checkCount < maxChecks && (!modalShown || privyModal)) {
              setTimeout(checkForAnalytics, 1000)
            }
          }

          setTimeout(checkForAnalytics, 1000)

          // Backup Privy direct call
          setTimeout(() => {
            if (typeof window !== "undefined" && (window as any).privy) {
              try {
                if (loginOptions) {
                  ;(window as any).privy.login(loginOptions)
                } else {
                  ;(window as any).privy.login()
                }
              } catch (err) {
                console.log("Backup privy call failed:", err)
              }
            }
          }, 100)
        } catch (error) {
          console.log("Main login attempt failed, trying fallback:", error)
          const connectButton = document.querySelector(
            '[title*="Connect" i], [title*="Wallet" i], button:contains("Connect")'
          )
          if (connectButton) {
            ;(connectButton as HTMLElement).click()
          }
        }
      }
    }, 10000)

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [searchParams, currentUser?.id, connectOrCreateWallet])

  // Cleanup timeout when user logs in
  useEffect(() => {
    if (currentUser?.id && timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }, [currentUser?.id])

  // Clear all flags when user successfully logs in
  useEffect(() => {
    if (currentUser?.id) {
      window.localStorage.removeItem(DISMISSAL_KEY)
      window.localStorage.removeItem(MODAL_SHOWN_KEY)
      modalTriggeredRef.current = false
      console.log("User logged in, clearing all flags")
    }
  }, [currentUser?.id])

  return null
}

export default DgbetaAutoLogin
